<template>
  <v-container fluid class="erp-page page">
    <div class="erp-page-header page-hero">
      <div>
        <h1 class="erp-page-title d-flex align-center">{{ directOrder ? 'Nuevo pedido de cuenta corriente' : (ordersOnly ? 'Presupuestos y pedidos de cuenta corriente' : 'Presupuestos') }} <HelpHint :text="directOrder ? 'Cargá el pedido directamente. Quedará presentado para los controles de crédito y bonificaciones antes de preparar la mercadería.' : (ordersOnly ? 'Consultá tus presupuestos y, cuando el cliente confirme, registrá la aceptación para crear un pedido.' : 'Prepará el presupuesto para tu cliente y seguí su aceptación desde Mis pedidos.')" /></h1>
        <p>{{ directOrder ? 'La aceptación comercial está implícita. Los controles de crédito, bonificaciones y stock se mantienen.' : (ordersOnly ? 'Primero enviás la propuesta; cuando el cliente acepta, se crea el pedido para revisión y preparación.' : 'Alta y edición de presupuestos comerciales con detalle de productos.') }}</p>
      </div>
      <div class="d-flex" style="gap: 10px">
        <v-btn v-if="ordersOnly" color="success" :to="{ name: 'sales-budgets' }"><v-icon left>mdi-plus</v-icon>Nuevo presupuesto</v-btn>
        <v-btn v-if="directOrder" text color="primary" :to="{ name: 'sales-order-management' }">Volver a gestión</v-btn>
        <v-btn color="primary" :loading="loading" @click="loadAll">Recargar</v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense outlined class="mb-4">{{ success }}</v-alert>

    <v-card v-if="!ordersOnly || editingOrder || directOrder" outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">{{ directOrder ? 'Datos del pedido directo' : (ordersOnly ? 'Presupuesto para cliente de cuenta corriente' : 'Nuevo / Editar presupuesto') }}</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field :value="form.docNumber || 'Se asigna al guardar'" label="Número interno" outlined dense readonly />
          </v-col>
          <v-col cols="12" md="4">
            <div class="d-flex align-start" style="gap: 8px;">
              <v-autocomplete
                v-model="form.currentAccountId"
                :items="customerOptions"
                :search-input.sync="customerSearchText"
                :loading="customerSearchLoading"
                item-text="label"
                item-value="id"
                clearable
                hide-no-data
                label="Cliente: código, nombre, CUIT o DNI"
                outlined
                dense
                @update:search-input="onCustomerSearchInput"
                @change="onBudgetCustomerChanged"
              />
              <v-btn color="primary" small class="mt-1" @click="openQuickCustomer">
                <v-icon left small>mdi-account-plus-outline</v-icon>
                Nuevo
              </v-btn>
            </div>
          </v-col>
          <v-col cols="12" md="2"><v-text-field v-model="form.issueDate" type="date" label="Fecha" outlined dense /></v-col>
          <v-col v-if="!directOrder" cols="12" md="2"><v-text-field v-model="form.validUntil" type="date" label="Válido hasta" outlined dense /></v-col>
          <v-col v-if="!directOrder" cols="12" md="2">
            <v-select v-model="form.status" :items="statusOptions" item-text="text" item-value="value" label="Estado" outlined dense />
          </v-col>
          <v-col v-if="selectedBudgetCustomer" cols="12">
            <v-sheet outlined rounded class="pa-3 grey lighten-5">
              <div class="subtitle-2 mb-2">
                <v-icon small color="primary" class="mr-1">mdi-account-card-outline</v-icon>
                Datos del cliente seleccionado
              </div>
              <v-row dense>
                <v-col cols="12" md="3"><strong>Cliente:</strong> {{ selectedBudgetCustomer.code }} - {{ selectedBudgetCustomer.name }}</v-col>
                <v-col cols="12" md="3"><strong>CUIT / DNI:</strong> {{ selectedBudgetCustomer.taxId || '—' }}</v-col>
                <v-col cols="12" md="3"><strong>Teléfono:</strong> {{ selectedBudgetCustomer.phone || '—' }}</v-col>
                <v-col cols="12" md="3"><strong>Email:</strong> {{ selectedBudgetCustomer.email || '—' }}</v-col>
                <v-col cols="12" md="6"><strong>Dirección:</strong> {{ selectedBudgetCustomer.address || '—' }}</v-col>
                <v-col cols="12" md="3"><strong>Condición IVA:</strong> {{ selectedBudgetCustomer.taxCondition || '—' }}</v-col>
                <v-col cols="12" md="3"><strong>Tipo de cliente:</strong> {{ selectedBudgetCustomer.clientType || '—' }}</v-col>
                <v-col v-if="ordersOnly" cols="12" md="3"><strong>Corredor asignado:</strong> {{ selectedBudgetCustomer.brokerName || 'A confirmar' }}</v-col>
                <v-col v-if="ordersOnly" cols="12" md="3"><strong>Límite de crédito:</strong> $ {{ formatAmount(selectedBudgetCustomer.creditLimit) }}</v-col>
                <v-col v-if="ordersOnly" cols="12" md="3"><strong>Saldo actual:</strong> $ {{ formatAmount(selectedBudgetCustomer.balance) }}</v-col>
                <v-col v-if="ordersOnly" cols="12" md="3"><strong>Plazo:</strong> {{ selectedBudgetCustomer.paymentTermsDays || 0 }} días</v-col>
              </v-row>
            </v-sheet>
          </v-col>
          <v-col cols="12"><v-text-field v-model="form.notes" label="Observaciones" outlined dense /></v-col>
          <v-col cols="12">
            <v-checkbox
              v-model="form.promptPaymentEnabled"
              dense
              hide-details
              label="Aplicar pronto pago: 24% adicional por pago dentro de 30 días"
              @change="onPromptPaymentChanged"
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              v-model="form.showTotal"
              dense
              hide-details
              label="Mostrar total general en el presupuesto impreso"
            />
          </v-col>
        </v-row>

        <v-divider class="my-3" />
        <h3 class="subtitle-2 mb-1">Catálogo de precios</h3>
        <p class="caption grey--text text--darken-1 mb-3">
          Elegí el cliente, buscá el artículo y agregalo con el precio de lista o con uno de los descuentos disponibles. La bonificación adicional ya está incluida en el precio final cuando corresponda.
        </p>

        <v-alert v-if="!form.currentAccountId" type="info" dense outlined class="mb-3">
          Seleccioná un cliente para consultar y agregar artículos al presupuesto.
        </v-alert>

        <template v-else>
          <v-row dense class="align-end">
            <v-col cols="12" md="3">
              <v-select
                v-model="budgetCatalog.priceListId"
                :items="priceListOptions"
                item-text="label"
                item-value="id"
                label="Lista de precios"
                outlined
                dense
                hide-details="auto"
                @change="fetchBudgetCatalog"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="budgetCatalog.selectedCode"
                :search-input.sync="budgetCatalog.searchText"
                :items="budgetCatalogSuggestions"
                :loading="budgetCatalogSuggestLoading"
                item-text="label"
                item-value="code"
                clearable
                hide-no-data
                label="Buscar artículo (código o nombre)"
                outlined
                dense
                hide-details="auto"
                @update:search-input="onBudgetCatalogSearchInput"
                @change="onBudgetCatalogOptionSelected"
              />
            </v-col>
            <v-col cols="12" md="1">
              <v-text-field v-model.number="budgetCatalog.quantity" type="number" min="0.01" step="0.01" label="Cant." outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="3">
              <v-btn color="primary" block :loading="budgetCatalogLoading" @click="fetchBudgetCatalog">Buscar</v-btn>
            </v-col>
          </v-row>

          <v-data-table
            class="mt-3 budget-catalog-table"
            :headers="budgetCatalogHeaders"
            :items="budgetCatalogRows"
            :loading="budgetCatalogLoading"
            item-key="productId"
            dense
            :footer-props="{ 'items-per-page-options': [10, 20, 50], 'items-per-page-text': 'Artículos por página:' }"
          >
            <template v-slot:item.stockAvailable="{ item }">
              <v-chip x-small :color="Number(item.stockAvailable) > 0 ? 'green lighten-5' : 'red lighten-5'" :text-color="Number(item.stockAvailable) > 0 ? 'green darken-3' : 'red darken-3'">
                {{ formatQuantity(item.stockAvailable) }}
              </v-chip>
            </template>
            <template v-slot:item.listPrice="{ item }">$ {{ formatAmount(item.price) }}</template>
            <template v-slot:item.finalPrice="{ item }">
              <div v-if="item.appliedBonus">
                <div class="caption grey--text text--darken-1">Lista: $ {{ formatAmount(item.price) }}</div>
                <div class="caption success--text font-weight-bold">Bonif. adicional {{ bonusLabel(item) }}</div>
              </div>
              <strong>$ {{ formatAmount(item.finalPrice) }}</strong>
            </template>
            <template v-for="discount in budgetMatrixDiscounts" v-slot:[`item.matrix_${discount}`]="{ item }">
              <v-btn :key="`matrix-${discount}`" text small color="primary" :disabled="!canAddCatalogItem(item)" @click="addCatalogItem(item, discount)">
                $ {{ formatAmount(matrixPrice(item, discount)) }}
              </v-btn>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small color="secondary" :disabled="!canAddCatalogItem(item)" @click="addCatalogItem(item, 0)">
                Agregar
              </v-btn>
            </template>
          </v-data-table>
        </template>

        <h3 class="subtitle-2 mt-5 mb-2">Ítems del {{ directOrder ? 'pedido' : 'presupuesto' }}</h3>

        <v-data-table :headers="itemHeaders" :items="form.items" item-key="_uid" dense class="mb-3">
          <template v-slot:item.productId="{ item }">{{ productLabel(item) }}</template>
          <template v-slot:item.quantity="{ item }">
            <v-text-field
              v-model.number="item.quantity"
              type="number"
              min="0.01"
              step="0.01"
              dense
              hide-details
              aria-label="Cantidad"
              style="max-width: 92px"
              @change="normalizeItemQuantity(item)"
            />
          </template>
          <template v-slot:item.unitPrice="{ item }">
            <v-text-field
              v-model.number="item.unitPrice"
              type="number"
              min="0"
              step="0.01"
              prefix="$"
              dense
              hide-details
              aria-label="Precio de lista"
              style="max-width: 120px"
              @change="normalizeCommercialLine(item)"
            />
          </template>
          <template v-slot:item.sourceListPrice="{ item }">$ {{ formatAmount(item.sourceListPrice == null ? item.unitPrice : item.sourceListPrice) }}</template>
          <template v-slot:item.articleDiscount="{ item }">{{ formatQuantity(item.sourceListPrice > 0 ? Math.max(0, (1 - item.unitPrice / item.sourceListPrice) * 100) : 0) }} %</template>
          <template v-slot:item.customerBonusPercent="{ item }"><v-text-field v-model.number="item.customerBonusPercent" :disabled="form.commercialDiscounts.some(x => x.kind === 'customer')" type="number" min="0" max="100" step="0.01" suffix="%" dense hide-details aria-label="Bonificación del cliente para el artículo" style="max-width:105px" @change="normalizeCommercialLine(item)" /></template>
          <template v-slot:item.discountPercent="{ item }">
            <v-text-field
              v-model.number="item.discountPercent"
              type="number"
              min="0"
              max="100"
              step="0.01"
              suffix="%"
              dense
              hide-details
              aria-label="Bonificación adicional"
              style="max-width: 105px"
              @change="normalizeCommercialLine(item)"
            />
          </template>
          <template v-slot:item.promptPaymentDiscountPercent="{ item }">{{ formatQuantity(item.promptPaymentDiscountPercent) }}%</template>
          <template v-slot:item.finalUnitPrice="{ item }">$ {{ formatAmount(finalUnitPrice(item)) }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="error" title="Quitar artículo" aria-label="Quitar artículo" @click="removeItem(item)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <v-card outlined class="pa-4 mb-4">
          <div class="subtitle-1 font-weight-bold">Descuentos sobre la compra</div>
          <div class="caption mb-3">Se aplican en orden sobre el saldo anterior. Los precios de los artículos se muestran antes de estos descuentos.</div>
          <v-row v-for="(discount, index) in form.commercialDiscounts" :key="index" dense align="center">
            <v-col cols="12" sm="7"><v-text-field v-model="discount.label" label="Referencia" outlined dense hide-details /></v-col>
            <v-col cols="8" sm="3"><v-text-field v-model.number="discount.percent" type="number" min="0" max="100" suffix="%" label="Descuento" outlined dense hide-details /></v-col>
            <v-col cols="4" sm="2"><v-btn text small color="error" @click="removeCommercialDiscount(index)">Quitar</v-btn></v-col>
          </v-row>
          <v-btn text small color="primary" class="mt-2" @click="addCommercialDiscount"><v-icon small left>mdi-plus</v-icon>Agregar descuento</v-btn>
          <div class="text-right mt-3"><div>Subtotal artículos: $ {{ formatAmount(quoteTotals.subtotal) }}</div>
            <div v-for="(discount, index) in quoteTotals.discounts" :key="`total-${index}`">{{ discount.label || 'Descuento' }} ({{ formatQuantity(discount.percent) }}%): − $ {{ formatAmount(discount.amount) }}</div>
            <strong>Total {{ directOrder ? 'pedido' : 'presupuesto' }}: $ {{ formatAmount(quoteTotals.total) }}</strong></div>
        </v-card>

        <div class="actions-row">
          <v-btn color="success" :loading="saving" @click="saveRow">{{ form.id ? "Actualizar" : (directOrder ? "Crear pedido y presentar" : "Guardar presupuesto") }}</v-btn>
          <v-btn text color="primary" @click="resetForm">{{ (ordersOnly || directOrder) ? 'Cancelar' : 'Limpiar' }}</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card v-if="ordersOnly" outlined class="page-card">
      <v-card-title class="subtitle-1">
        <span>Presupuestos y pedidos</span>
        <v-spacer />
        <v-btn text small color="primary" @click="clearDateFilter">Limpiar fechas</v-btn>
      </v-card-title>
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3"><v-text-field v-model="historyFilters.dateFrom" type="date" label="Desde" outlined dense hide-details /></v-col>
          <v-col cols="12" sm="4" md="3"><v-text-field v-model="historyFilters.dateTo" type="date" label="Hasta" outlined dense hide-details /></v-col>
          <v-col cols="12" sm="4" md="3"><v-select v-model="historyFilters.originType" :items="originOptions" item-text="text" item-value="value" clearable label="Origen" outlined dense hide-details /></v-col>
          <v-col cols="12" sm="4" md="2"><v-btn color="primary" block :loading="loading" @click="loadAll">Buscar</v-btn></v-col>
        </v-row>
      </v-card-text>
      <v-data-table :headers="headers" :items="rows" :loading="loading" item-key="id" dense>
        <template v-slot:item.currentAccount="{ item }"><strong>{{ item.currentAccount ? `${item.currentAccount.code} - ${item.currentAccount.name}` : "Consumidor final" }}</strong><div v-if="ordersOnly" class="caption">Corredor: {{ item.salesAgentName || item.sellerName || 'Sin asignar' }}</div></template>
        <template v-slot:item.documentKind="{ item }">{{ item.documentKind === 'quote' ? 'Presupuesto' : 'Pedido' }}</template>
        <template v-slot:item.originType="{ item }">
          <v-chip x-small :color="item.originType === 'ONLINE_SALES' ? 'blue lighten-4' : 'grey lighten-3'" :text-color="item.originType === 'ONLINE_SALES' ? 'blue darken-3' : 'grey darken-3'">
            {{ item.originLabel || (item.originType === 'ONLINE_SALES' ? 'Venta online' : 'Manual') }}
          </v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="budgetStatusMeta(item).color" dark>{{ budgetStatusMeta(item).text }}</v-chip>
        </template>
        <template v-slot:item.total="{ item }">{{ formatAmount(item.total) }}</template>
        <template v-slot:item.actions="{ item }">
          <v-btn v-if="canEditDraft(item)" text small color="primary" @click="editRow(item)">Editar</v-btn>
          <v-btn v-if="isAdmin && canEditDraft(item)" text small color="error" @click="deleteRow(item)">Eliminar</v-btn>
          <v-btn v-if="ordersOnly && item.documentKind === 'quote' && ['draft','partially_accepted'].includes(item.workflowStatus)" text small color="success" :loading="acceptingQuote === item.id" @click="openAcceptQuote(item)"><v-icon left small>mdi-check-circle-outline</v-icon>Registrar aceptación</v-btn>
          <v-btn 
            v-if="item.documentKind !== 'quote' && (item.workflowStatus === 'draft' || !item.workflowStatus)"
            text 
            small 
            color="orange" 
            :loading="sendingToCashier === item.id"
            @click="openSubmitDialog(item)"
          >
            {{ ordersOnly ? 'Presentar pedido a revisión' : 'Enviar a Caja' }}
          </v-btn>
          <v-btn v-else-if="item.workflowStatus === 'to_pay'" text small color="success" disabled>
            En Caja
          </v-btn>
          <v-btn 
            v-if="item.workflowStatus === 'to_pay'"
            text 
            small 
            color="info" 
            @click="previewBudget(item)"
          >
            Previsualizar
          </v-btn>
          <v-btn 
            v-if="item.workflowStatus === 'to_pay'"
            text 
            small 
            color="warning" 
            @click="openEditCashier(item)"
          >
            Editar
          </v-btn>
          <v-btn v-if="!ordersOnly" text small color="secondary" @click="openConvertDialog(item)">Emitir</v-btn>
          <v-btn v-if="ordersOnly || isAdmin" text small color="indigo" @click="printBudget(item)">Descargar PDF</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="discountRemoval.open" max-width="480" persistent>
      <v-card><v-card-title>Quitar descuento</v-card-title><v-card-text>
        <p>Indicá por qué quitás <strong>{{ discountRemoval.label }}</strong>. La referencia quedará guardada en el presupuesto.</p>
        <v-textarea v-model="discountRemoval.reason" label="Motivo" outlined dense rows="2" counter="300" maxlength="300" />
      </v-card-text><v-card-actions><v-spacer/><v-btn text @click="discountRemoval.open=false">Volver</v-btn><v-btn color="error" :disabled="!discountRemoval.reason.trim()" @click="confirmRemoveCommercialDiscount">Quitar descuento</v-btn></v-card-actions></v-card>
    </v-dialog>
    <v-dialog v-model="acceptQuoteDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="subtitle-1"><v-icon left color="success">mdi-check-circle-outline</v-icon>Registrar aceptación del presupuesto</v-card-title>
        <v-card-text>
          <p v-if="quoteToAccept">Vas a crear un pedido desde <strong>{{ quoteToAccept.docNumber }}</strong> para <strong>{{ quoteToAccept.currentAccount?.name || 'el cliente' }}</strong>.</p>
          <v-alert type="info" dense outlined><strong>Al confirmar se crea un pedido.</strong> Indicá las cantidades aceptadas. Se conservan los precios y bonificaciones; el pedido pasa a revisión de crédito y bonificaciones. El presupuesto mantiene el saldo no aceptado.</v-alert>
          <v-simple-table v-if="quoteAcceptItems.length" dense class="mt-3"><thead><tr><th>Artículo</th><th>Ya aceptado</th><th>Pendiente</th><th style="width:150px">Aceptado ahora</th></tr></thead><tbody><tr v-for="line in quoteAcceptItems" :key="line.sourceItemId"><td>{{ line.label }}</td><td>{{ line.accepted }}</td><td>{{ line.remaining }}</td><td><v-text-field v-model.number="line.quantity" type="number" min="0" :max="line.remaining" step="0.01" dense hide-details /></td></tr></tbody></v-simple-table>
        </v-card-text>
        <v-card-actions><v-spacer/><v-btn text :disabled="Boolean(acceptingQuote)" @click="acceptQuoteDialog=false">Volver al presupuesto</v-btn><v-btn color="success" :loading="Boolean(acceptingQuote)" @click="confirmAcceptQuote"><v-icon left>mdi-file-document-plus-outline</v-icon>Aceptar y presentar a revisión</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="budgetCreatedDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="subtitle-1 success white--text">
          <v-icon left color="white">mdi-check-circle-outline</v-icon>
          {{ directOrder ? 'Pedido creado' : 'Presupuesto guardado' }}
        </v-card-title>
        <v-card-text class="pt-5">
          <p class="mb-2">{{ directOrder ? 'El pedido quedó presentado. Si requiere una excepción, aparecerá en Créditos y autorizaciones; si se aprobó automáticamente, ya puede prepararse.' : 'La propuesta quedó guardada. Cuando el cliente confirme, ingresá en Ventas → Mis pedidos y elegí “Registrar aceptación”.' }}</p>
          <div class="text-center py-3 grey lighten-4 rounded">
            <div class="caption grey--text text--darken-1">{{ directOrder ? 'Número de pedido' : 'Número interno' }}</div>
            <div class="headline font-weight-bold">{{ createdBudget && createdBudget.docNumber }}</div>
          </div>
          <p v-if="!directOrder" class="caption grey--text text--darken-1 mt-4 mb-0">
            Podés descargar el PDF comercial ahora o más tarde desde presupuestos y pedidos.
          </p>
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-btn v-if="!directOrder && (ordersOnly || isAdmin)" color="primary" :loading="downloadingBudget" @click="downloadCreatedBudget">
            <v-icon left small>mdi-download</v-icon>
            Descargar PDF
          </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="closeCreatedBudgetDialog">Continuar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="submitDialog" max-width="540" persistent>
      <v-card v-if="orderToSubmit">
        <v-card-title class="headline primary white--text"><v-icon left color="white">mdi-send-check-outline</v-icon>Confirmar presentación del pedido</v-card-title>
        <v-card-text class="pt-5">
          <p>Vas a presentar el pedido <strong>{{ orderToSubmit.docNumber }}</strong> de <strong>{{ orderToSubmit.currentAccount?.name || 'cliente sin nombre' }}</strong>.</p>
          <v-alert type="info" outlined dense>Se controlarán automáticamente el límite de crédito, la deuda y el descuento otorgado. Si hace falta una excepción, el pedido quedará en la bandeja de autorizaciones.</v-alert>
        </v-card-text>
        <v-card-actions class="pa-4"><v-spacer/><v-btn text @click="submitDialog=false">Volver</v-btn><v-btn color="primary" :loading="sendingToCashier===orderToSubmit.id" @click="confirmSubmit">Presentar pedido</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="submitSuccessDialog" max-width="460"><v-card class="text-center pa-6"><v-icon size="58" color="success">mdi-check-circle</v-icon><h2 class="mt-3">Pedido presentado</h2><p>{{ submitSuccessMessage }}</p><v-btn color="success" @click="submitSuccessDialog=false">Entendido</v-btn></v-card></v-dialog>

    <v-dialog v-model="convertDialog" max-width="760">
      <v-card>
        <v-card-title class="subtitle-1">Emitir presupuesto por flujo unificado</v-card-title>
        <v-card-text>
          <v-alert v-if="convertError" type="error" dense outlined class="mb-3">{{ convertError }}</v-alert>
          <v-alert type="info" dense outlined class="mb-3">
            La emisión asigna el comprobante, procesa CAE cuando corresponde, stock, cobro y contabilidad en un único flujo. El número y los datos fiscales no se cargan manualmente.
          </v-alert>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="convertForm.warehouseId"
                :items="warehouseOptions"
                item-text="label"
                item-value="id"
                clearable
                label="Depósito"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6"><v-select v-model="convertForm.mode" :items="conversionModeOptions" item-text="text" item-value="value" label="Modalidad" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="convertForm.issueDate" type="date" label="Fecha" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="convertForm.paymentMethod" label="Forma de pago" outlined dense /></v-col>
            <v-col cols="12"><v-text-field v-model="convertForm.notes" label="Observaciones factura" outlined dense /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeConvertDialog">Cancelar</v-btn>
          <v-btn color="success" :loading="converting" @click="convertBudget">Emitir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo Premium de Error / Validación de Stock -->
    <v-dialog v-model="errorDialog" max-width="500">
      <v-card style="border-top: 5px solid #ff5252; border-radius: 12px; overflow: hidden;">
        <v-card-title class="headline red--text text--darken-1 d-flex align-center pb-3">
          <v-icon color="red" class="mr-2" large>mdi-alert-circle</v-icon>
          <span class="font-weight-bold" style="font-size: 18px;">Orden Rechazada por Falta de Stock</span>
        </v-card-title>
        <v-card-text class="pt-2">
          <p class="grey--text text--darken-3 mb-3" style="font-size: 14px; line-height: 1.5;">
            El sistema de inventario rechazó la reserva de stock para este pedido debido a unidades insuficientes:
          </p>
          <v-alert type="error" text class="mb-0 font-weight-bold" style="font-size: 14px; border-radius: 8px; line-height: 1.5;">
            {{ activeErrorMsg }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="grey lighten-5 px-4 py-3">
          <v-spacer />
          <v-btn color="red" class="px-6 white--text rounded-pill font-weight-bold elevation-1" @click="errorDialog = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <v-dialog v-model="previewDialog" max-width="760">
      <v-card>
        <v-card-title class="subtitle-1 primary white--text d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-file-eye-outline</v-icon>
          Previsualizar pedido
        </v-card-title>
        <v-card-text v-if="previewData" class="pt-4">
          <div class="d-flex flex-wrap justify-space-between mb-3" style="gap: 12px;">
            <div>
              <div class="caption grey--text text--darken-1">Pedido</div>
              <div class="subtitle-1 font-weight-bold">{{ previewData.docNumber }}</div>
            </div>
            <div class="text-md-right">
              <div class="caption grey--text text--darken-1">Cliente</div>
              <div class="subtitle-1 font-weight-bold">{{ previewData.customer && previewData.customer.name ? previewData.customer.name : "Consumidor final" }}</div>
              <div class="caption">Tipo: {{ previewData.clientType || "CM" }}</div>
            </div>
          </div>

          <v-simple-table dense class="preview-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th class="text-right">Cantidad</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(it, idx) in previewData.items || []" :key="idx">
                  <td>{{ it.name || it.description || "Producto" }}</td>
                  <td class="text-right">{{ formatAmount(it.quantity) }}</td>
                  <td class="text-right">$ {{ formatAmount(it.lineTotal) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-divider class="my-4" />
          <div class="preview-totals ml-auto">
            <div><span>Subtotal</span><strong>$ {{ formatAmount(previewData.totals && previewData.totals.subtotal) }}</strong></div>
            <div><span>Descuentos</span><strong>− $ {{ formatAmount(previewData.totals && previewData.totals.discount) }}</strong></div>
            <div class="total"><span>Total</span><strong>$ {{ formatAmount(previewData.totals && previewData.totals.total) }}</strong></div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="previewDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="customerDialog" max-width="520">
      <v-card>
        <v-card-title class="subtitle-1 primary white--text">Nuevo cliente</v-card-title>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12"><v-text-field v-model="custForm.name" label="Nombre / Razón Social" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="custForm.taxId" label="CUIT / DNI" outlined dense /></v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="custForm.taxCondition"
                :items="['Consumidor Final', 'Responsable Inscripto', 'Monotributista', 'Exento']"
                label="Condición IVA"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12"><v-text-field v-model="custForm.address" label="Dirección" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="custForm.phone" label="Teléfono" outlined dense /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="custForm.email" label="Email" outlined dense /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="customerDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingCustomer" @click="saveQuickCustomer">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editCashierDialog" max-width="700">
      <v-card>
        <v-card-title class="subtitle-1">Editar desde Caja</v-card-title>
        <v-card-text>
          <v-alert v-if="editCashierError" type="error" dense outlined class="mb-3">{{ editCashierError }}</v-alert>
          <v-select v-model="editCashierForm.currentAccountId" :items="customerOptions" item-text="label" item-value="id" clearable label="Cliente" outlined dense />
          <v-select v-model="editCashierForm.paymentMethod" :items="['Efectivo','Transferencia','Tarjeta','Cuenta Corriente']" label="Medio de Pago" outlined dense />
          <v-select v-model="editCashierForm.posNumber" :items="posOptions" item-text="label" item-value="number" clearable label="Punto de Venta" outlined dense />
          <h4 class="subtitle-2 mt-3 mb-2">Ítems</h4>
          <v-simple-table dense>
            <template v-slot:default>
              <thead><tr><th>Producto</th><th class="text-center">Cant.</th><th class="text-right">P.Unit</th><th class="text-right">Desc%</th><th></th></tr></thead>
              <tbody>
                <tr v-for="(it, idx) in editCashierForm.items" :key="idx">
                  <td>{{ productLabel(it) }}</td>
                  <td><v-text-field v-model.number="it.quantity" type="number" dense hide-details style="width:70px" class="mx-auto" /></td>
                  <td><v-text-field v-model.number="it.unitPrice" type="number" dense hide-details style="width:90px" class="ml-auto" /></td>
                  <td><v-text-field v-model.number="it.discountPercent" type="number" dense hide-details style="width:70px" class="ml-auto" /></td>
                  <td><v-btn icon small color="error" @click="editCashierForm.items.splice(idx,1)"><v-icon small>mdi-delete</v-icon></v-btn></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editCashierDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="editCashierSaving" @click="saveEditCashier">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>Confirmar eliminación</v-card-title>
        <v-card-text>¿Confirmás eliminar {{ deleteTarget ? deleteTarget.docNumber : 'este documento' }}? Esta acción sólo está disponible mientras el documento puede eliminarse.</v-card-text>
        <v-card-actions><v-spacer/><v-btn text @click="deleteDialog = false; deleteTarget = null">Volver</v-btn><v-btn color="error" :loading="deleting" @click="confirmDelete">Eliminar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { commercialDocumentsService } from "../services/commercialDocuments";
import { currentAccountsService } from "../services/currentAccounts";
import { productsService } from "../services/products";
import { warehousesService } from "../services/warehouses";
import { salesService } from "../services/sales";
import { pointsOfSaleService } from "../services/pointsOfSale";
import { priceListsService } from "../services/priceLists";
import { pricingStockService } from "../services/pricingStock";
import api from "../services/api";
import HelpHint from "@/components/HelpHint.vue";

function nullableText(v) {
  const s = String(v || "").trim();
  return s || null;
}

export default {
  name: "SalesBudgets",
  components: { HelpHint },
  props: { ordersOnly: { type: Boolean, default: false }, directOrder: { type: Boolean, default: false } },
  data() {
    return {
      loading: false,
      saving: false,
      discountRemoval: { open: false, index: -1, label: '', reason: '' },
      error: "",
      success: "",
      historyFilters: { dateFrom: "", dateTo: "", originType: "" },
      rows: [],
      customers: [],
      customerSuggestions: [],
      customerSearchText: "",
      customerSearchLoading: false,
      customerSearchDebounce: null,
      customerSearchRequest: 0,
      products: [],
      priceLists: [],
      productSearchText: "",
      budgetCatalogLoading: false,
      budgetCatalogSuggestLoading: false,
      budgetCatalogRows: [],
      budgetCatalogSuggestions: [],
      budgetCatalog: {
        priceListId: "",
        selectedCode: "",
        searchText: "",
        exactSelected: false,
        quantity: 1,
      },
      budgetCreatedDialog: false,
      acceptingQuote: null,
      acceptQuoteDialog: false,
      quoteToAccept: null,
      quoteAcceptItems: [],
      createdBudget: null,
      downloadingBudget: false,
      warehouses: [],
      form: this.emptyForm(),
      itemForm: this.emptyItemForm(),
      convertDialog: false,
      converting: false,
      convertError: "",
      selectedBudget: null,
      sendingToCashier: null,
      submitDialog: false,
      submitSuccessDialog: false,
      submitSuccessMessage: "",
      orderToSubmit: null,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      convertForm: this.emptyConvertForm(),
      previewDialog: false,
      previewData: null,
      errorDialog: false,
      activeErrorMsg: "",
      editCashierDialog: false,
      editCashierSaving: false,
      editCashierError: "",
      editingOrder: false,
      customerDialog: false,
      savingCustomer: false,
      custForm: {
        name: "",
        taxId: "",
        taxCondition: "Consumidor Final",
        address: "",
        phone: "",
        email: "",
      },
      editCashierForm: { id: null, currentAccountId: "", paymentMethod: "", posNumber: null, items: [] },
      pointsOfSale: [],
      statusOptions: [
        { text: "Borrador", value: "draft" },
        { text: "Emitido", value: "issued" },
        { text: "Cancelado", value: "cancelled" },
      ],
      conversionModeOptions: [
        { text: "Facturado (solicita CAE)", value: "FISCAL" },
        { text: "Interno", value: "INTERNAL" },
        { text: "Mixto", value: "MIXED" },
      ],
      originOptions: [
        { text: "Venta online", value: "ONLINE_SALES" },
        { text: "Manual", value: "MANUAL" },
      ],
      headers: [
        { text: "Número", value: "docNumber" },
        { text: "Documento", value: "documentKind" },
        { text: "Fecha", value: "issueDate" },
        { text: "Cliente", value: "currentAccount" },
        { text: "Origen", value: "originType" },
        { text: "Estado", value: "status" },
        { text: "Total", value: "total" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    isCurrentAccountMode() { return this.ordersOnly || this.directOrder; },
    isAdmin() {
      return (this.$store.state.auth.user?.roles || []).map((role) => String(role).toLowerCase()).includes("admin");
    },
    customerOptions() {
      const selected = this.customers.find((x) => x.id === this.form.currentAccountId);
      const byId = new Map();
      [...this.customerSuggestions, selected].filter(Boolean).forEach((customer) => {
        const label = [customer.code, customer.name, customer.taxId].filter(Boolean).join(" - ");
        byId.set(customer.id, { id: customer.id, label: label || customer.name || "Cliente sin nombre" });
      });
      return Array.from(byId.values());
    },
    productOptions() {
      return this.products.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    priceListOptions() {
      return this.priceLists
        .filter((x) => Boolean(x.isActive))
        .map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    selectedBudgetCustomer() {
      return this.customers.find((x) => x.id === this.form.currentAccountId) || null;
    },
    budgetCustomerType() {
      const type = String((this.selectedBudgetCustomer && this.selectedBudgetCustomer.clientType) || "").trim().toLowerCase();
      if (["final", "final_consumer", "consumidor_final", "consumidor"].includes(type)) return "final_consumer";
      if (["account", "account_current", "cuenta_corriente", "corriente"].includes(type)) return "account_current";
      return "all";
    },
    budgetMatrixDiscounts() {
      const list = this.priceLists.find((x) => x.id === this.budgetCatalog.priceListId);
      if (!list) return [];
      const discounts = new Set();
      if (list.attributes) {
        String(list.attributes).split(",")
          .map((value) => parseFloat(value.trim()))
          .filter((value) => Number.isFinite(value) && value !== 0)
          .forEach((value) => discounts.add(Math.abs(value)));
      }
      if (list.calculationExpression) {
        const match = String(list.calculationExpression).match(/price\s*\*\s*([0-9.]+)/i);
        const factor = match && match[1] ? parseFloat(match[1]) : NaN;
        if (Number.isFinite(factor) && factor > 0 && factor < 1) discounts.add(Number(((1 - factor) * 100).toFixed(2)));
      }
      return Array.from(discounts).sort((a, b) => a - b);
    },
    budgetCatalogHeaders() {
      const headers = [
        { text: "Código", value: "code" },
        { text: "Descripción", value: "name" },
        { text: "Stock disp.", value: "stockAvailable", align: "center" },
        { text: "Precio lista", value: "listPrice", align: "end" },
        { text: "Precio final", value: "finalPrice", align: "end" },
      ];
      this.budgetMatrixDiscounts.forEach((discount) => {
        headers.push({ text: `Lista -${discount}%`, value: `matrix_${discount}`, align: "end", sortable: false });
      });
      headers.push({ text: "", value: "actions", align: "end", sortable: false });
      return headers;
    },
    warehouseOptions() {
      return this.warehouses.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    posOptions() {
      return this.pointsOfSale.filter(p => p.isActive).map((x) => ({ number: x.number, label: `${x.number} - ${x.name || 'POS'}` }));
    },
    docTotal() {
      return this.form.items.reduce((acc, item) => acc + this.calcLineTotal(item), 0);
    },
    quoteTotals() {
      let running = Math.round(this.form.items.reduce((sum, item) => sum + this.calcLineTotal(item), 0) * 100);
      const subtotal = running / 100;
      const discounts = this.form.commercialDiscounts.map(discount => {
        const percent = Math.max(0, Math.min(100, Number(discount.percent) || 0));
        const amount = Math.round(running * percent * 10000 / 1000000);
        running -= amount;
        return { ...discount, percent, amount: amount / 100 };
      });
      return { subtotal, discounts, total: running / 100 };
    },
    itemHeaders() {
      const headers = [
        { text: "Producto", value: "productId" },
        { text: "Cantidad", value: "quantity" },
        { text: "Precio lista", value: "sourceListPrice" },
        { text: "Bonificación de cuenta", value: "customerBonusPercent" },
        { text: "Bonificación oferta", value: "articleDiscount" },
        { text: "Bonificación adicional", value: "discountPercent" },
      ];
      headers.push(
        { text: "Precio final", value: "finalUnitPrice" },
        { text: "Acciones", value: "actions", sortable: false },
      );
      return headers;
    },
  },
  created() {
    this.loadAll();
  },
  watch: {
    ordersOnly() {
      this.resetForm();
      this.loadAll();
    },
  },
  methods: {
    emptyForm() {
      return {
        id: "",
        documentKind: this.directOrder ? "budget" : "quote",
        salesChannel: "CURRENT_ACCOUNT",
        docNumber: "",
        currentAccountId: "",
        issueDate: new Date().toISOString().slice(0, 10),
        validUntil: this.directOrder ? "" : this.defaultValidUntil(),
        status: "draft",
        notes: "",
        promptPaymentEnabled: false,
        promptPaymentDiscountPercent: 0,
        commercialDiscounts: [],
        commercialDiscountChanges: [],
        showTotal: true,
        items: [],
      };
    },
    emptyItemForm() {
      return {
        productId: "",
        quantity: 1,
        unitPrice: 0,
        discountPercent: 0,
        notes: "",
      };
    },
    emptyCustomerForm() {
      return {
        name: "",
        taxId: "",
        taxCondition: "Consumidor Final",
        address: "",
        phone: "",
        email: "",
      };
    },
    emptyConvertForm() {
      return {
        warehouseId: "",
        issueDate: new Date().toISOString().slice(0, 10),
        mode: "FISCAL",
        paymentMethod: "",
        notes: "",
      };
    },
    defaultValidUntil() {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      return date.toISOString().slice(0, 10);
    },
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    getError(error) {
      if (error && error.response && error.response.data && error.response.data.error) return error.response.data.error;
      if (error && error.response && error.response.data && error.response.data.message) return error.response.data.message;
      if (error && error.message) return error.message;
      return "No pudimos completar la operación.";
    },
    formatAmount(value) {
      const n = Number(value || 0);
      return Number.isFinite(n) ? n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0,00";
    },
    calcLineTotal(item) {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      const discount = Number(item.discountPercent || 0);
      const base = quantity * unitPrice;
      const baseCents = Math.round((base + Number.EPSILON) * 100);
      const afterDiscount = baseCents - Math.round(baseCents * Math.round(discount * 10000) / 1000000);
      return (afterDiscount - Math.round(afterDiscount * Math.round(Number(item.customerBonusPercent || 0) * 10000) / 1000000)) / 100;
    },
    finalUnitPrice(item) {
      const price = Number(item.unitPrice || 0);
      const discount = Number(item.discountPercent || 0);
      return Number((price * (1 - discount / 100) * (1 - Number(item.customerBonusPercent || 0) / 100)).toFixed(2));
    },
    onPromptPaymentChanged(enabled) {
      if (!enabled) {
        const index = this.form.commercialDiscounts.findIndex(item => item.kind === 'prompt_payment');
        if (index >= 0) { this.form.promptPaymentEnabled = true; this.removeCommercialDiscount(index); return; }
      }
      const discount = enabled ? 24 : 0;
      this.form.promptPaymentDiscountPercent = discount;
      this.form.commercialDiscounts = this.form.commercialDiscounts.filter((item) => item.kind !== 'prompt_payment');
      if (enabled) this.form.commercialDiscounts.push({ kind: 'prompt_payment', label: 'Pronto pago', percent: discount });
    },
    removeCommercialDiscount(index) {
      const selected = this.form.commercialDiscounts[index];
      if (!selected) return;
      this.discountRemoval = { open: true, index, label: selected.label || 'descuento', reason: '' };
    },
    confirmRemoveCommercialDiscount() {
      const removed = this.form.commercialDiscounts.splice(this.discountRemoval.index, 1)[0];
      if (removed) this.form.commercialDiscountChanges.push({ action: 'removed', label: removed.label || 'Descuento', percent: Number(removed.percent || 0), reason: this.discountRemoval.reason.trim() });
      if (removed && removed.kind === 'prompt_payment') {
        this.form.promptPaymentEnabled = false;
        this.form.promptPaymentDiscountPercent = 0;
      }
      this.discountRemoval.open = false;
    },
    addCommercialDiscount() {
      const promptIndex = this.form.commercialDiscounts.findIndex(row => row.kind === 'prompt_payment');
      this.form.commercialDiscounts.splice(promptIndex < 0 ? this.form.commercialDiscounts.length : promptIndex, 0, { kind: 'additional', label: '', percent: 0 });
    },
    productLabelById(id) {
      const row = this.products.find((x) => x.id === id);
      return row ? `${row.code} - ${row.name}` : id;
    },
    productLabel(item) {
      if (item && item.productCode && item.productName) return `${item.productCode} - ${item.productName}`;
      if (item && item.product && item.product.code && item.product.name) return `${item.product.code} - ${item.product.name}`;
      return this.productLabelById(item && item.productId);
    },
    normalizeProductSearch(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    },
    filterProductOption(item, queryText) {
      const query = this.normalizeProductSearch(queryText);
      if (!query) return true;
      return this.normalizeProductSearch(item.code).includes(query)
        || this.normalizeProductSearch(item.name).includes(query);
    },
    selectUniqueProductFromSearch() {
      if (this.itemForm.productId || !this.productSearchText) return;
      const matches = this.productOptions.filter((item) => this.filterProductOption(item, this.productSearchText));
      if (matches.length === 1) this.itemForm.productId = matches[0].id;
    },
    focusProductSearch() {
      this.$nextTick(() => {
        const search = this.$refs.productSearch;
        if (search && typeof search.focus === "function") search.focus();
      });
    },
    onBudgetCustomerChanged() {
      this.form.commercialDiscounts = this.form.commercialDiscounts.filter(item => item.kind !== 'customer');
      const customerBonus = Number((this.selectedBudgetCustomer || {}).habitualBonusPercent || 0);
      this.form.items.forEach(item => { item.customerBonusPercent = customerBonus; });
      this.budgetCatalog.selectedCode = "";
      this.budgetCatalog.searchText = "";
      this.budgetCatalog.exactSelected = false;
      this.budgetCatalogSuggestions = [];
      this.budgetCatalogRows = [];
      if (this.form.currentAccountId && this.budgetCatalog.priceListId) this.fetchBudgetCatalog();
    },
    onCustomerSearchInput(search) {
      const query = String(search || "").trim();
      this.customerSearchText = search || "";
      if (this.customerSearchDebounce) clearTimeout(this.customerSearchDebounce);
      if (!query) {
        this.customerSuggestions = this.customers.slice(0, 30);
        return;
      }
      this.customerSearchDebounce = setTimeout(() => this.fetchCustomerSuggestions(query), 250);
    },
    async fetchCustomerSuggestions(query) {
      const requestId = ++this.customerSearchRequest;
      this.customerSearchLoading = true;
      try {
        const response = await currentAccountsService.list({ accountType: "customer", isActive: true, q: query });
        const rows = this.unwrap(response) || [];
        if (requestId !== this.customerSearchRequest) return;
        this.customerSuggestions = rows;
        const knownCustomers = new Map(this.customers.map((customer) => [customer.id, customer]));
        rows.forEach((customer) => knownCustomers.set(customer.id, customer));
        this.customers = Array.from(knownCustomers.values());
      } catch (_error) {
        if (requestId === this.customerSearchRequest) this.customerSuggestions = [];
      } finally {
        if (requestId === this.customerSearchRequest) this.customerSearchLoading = false;
      }
    },
    async onBudgetCatalogSearchInput(search) {
      this.budgetCatalog.searchText = search || "";
      this.budgetCatalog.exactSelected = false;
      if (this.budgetCatalog.selectedCode && search !== this.budgetCatalog.selectedCode) this.budgetCatalog.selectedCode = "";
      if (!search || String(search).trim().length < 2 || !this.budgetCatalog.priceListId) {
        this.budgetCatalogSuggestions = [];
        return;
      }
      this.budgetCatalogSuggestLoading = true;
      try {
        const response = await pricingStockService.getSalesCatalogSuggest({
          q: search,
          priceListId: this.budgetCatalog.priceListId,
        });
        const rows = this.unwrap(response) || [];
        this.budgetCatalogSuggestions = rows.map((row) => ({ ...row, label: `${row.code} - ${row.name}` }));
      } catch (_error) {
        this.budgetCatalogSuggestions = [];
      } finally {
        this.budgetCatalogSuggestLoading = false;
      }
    },
    onBudgetCatalogOptionSelected(value) {
      if (!value) return;
      this.budgetCatalog.selectedCode = value;
      this.budgetCatalog.searchText = value;
      this.budgetCatalog.exactSelected = true;
      this.fetchBudgetCatalog();
    },
    async fetchBudgetCatalog() {
      if (!this.form.currentAccountId) return;
      if (!this.budgetCatalog.priceListId) {
        this.error = "Seleccioná una lista de precios para consultar el catálogo.";
        this.budgetCatalogRows = [];
        return;
      }
      this.budgetCatalogLoading = true;
      this.error = "";
      try {
        const q = this.budgetCatalog.selectedCode || this.budgetCatalog.searchText || undefined;
        const response = await pricingStockService.getSalesCatalog({
          priceListId: this.budgetCatalog.priceListId,
          customerType: this.budgetCustomerType,
          quantity: Number(this.budgetCatalog.quantity || 1),
          q,
          exactCode: Boolean(this.budgetCatalog.exactSelected && this.budgetCatalog.selectedCode),
          includeZero: true,
          sortBy: "code",
          sortDir: "asc",
          limit: this.budgetCatalog.exactSelected ? 60 : 200,
        });
        const data = this.unwrap(response) || {};
        this.budgetCatalogRows = data.rows || [];
      } catch (error) {
        this.budgetCatalogRows = [];
        this.error = this.getError(error);
      } finally {
        this.budgetCatalogLoading = false;
      }
    },
    canAddCatalogItem(item) {
      return item && item.productId && Number(item.stockAvailable) > 0 && Number(item.canSell) !== 0;
    },
    matrixPrice(item, discount) {
      return Number((Number(item.finalPrice || 0) * (1 - Number(discount || 0) / 100)).toFixed(4));
    },
    addCatalogItem(item, discount) {
      if (!this.canAddCatalogItem(item)) {
        this.error = "El artículo no tiene stock disponible o no está habilitado para la venta.";
        return;
      }
      const quantity = Number(this.budgetCatalog.quantity || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        this.error = "Ingresá una cantidad válida.";
        return;
      }
      const discountPercent = Number(discount || 0);
      this.form.items.push({
        _uid: `${Date.now()}-${Math.random()}`,
        productId: item.productId,
        productCode: item.code || "",
        productName: item.name || "",
        quantity,
        unitPrice: Number(item.finalPrice || 0),
        sourceListPrice: Number(item.price || 0),
        catalogUnitPrice: Number(item.finalPrice || 0),
        discountPercent,
        customerBonusPercent: this.form.commercialDiscounts.some(x => x.kind === 'customer') ? null : Number((this.selectedBudgetCustomer || {}).habitualBonusPercent || 0),
        promptPaymentDiscountPercent: 0,
        notes: null,
      });
      this.success = discountPercent > 0
        ? `${item.code} agregado con descuento de lista del ${this.formatQuantity(discountPercent)}%.`
        : `${item.code} agregado al presupuesto con el precio final de lista.`;
    },
    formatQuantity(value) {
      const number = Number(value || 0);
      return Number.isFinite(number)
        ? number.toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        : "0";
    },
    bonusLabel(item) {
      const bonus = item && item.appliedBonus;
      if (!bonus) return "";
      const value = Number(bonus.bonusValue || 0);
      return bonus.bonusType === "amount" ? `$ ${this.formatAmount(value)}` : `${this.formatQuantity(value)}%`;
    },
    addItemFromSearch() {
      this.selectUniqueProductFromSearch();
      this.addItem();
    },
    budgetStatusMeta(item) {
      const workflow = String(item && item.workflowStatus ? item.workflowStatus : "").toLowerCase();
      const status = String(item && item.status ? item.status : "").toLowerCase();
      const map = {
        draft: { text: "Borrador", color: "grey" },
        accepted: { text: "Aceptado completamente", color: "success" },
        partially_accepted: { text: "Aceptado parcialmente", color: "teal" },
        expired: { text: "Vencido", color: "grey" },
        submitted: { text: "En autorización", color: "primary" },
        approved: { text: "Aprobado", color: "success" },
        credit_hold: { text: "Retenido por crédito", color: "orange" },
        to_pay: { text: "En caja", color: "orange" },
        partially_invoiced: { text: "Facturado parcialmente", color: "blue" },
        rejected: { text: "Rechazado", color: "error" },
        invoiced: { text: "Facturado", color: "success" },
        cancelled: { text: "Anulado", color: "error" },
        issued: { text: "Emitido", color: "info" },
        converted: { text: "Convertido", color: "success" },
      };
      return map[workflow] || map[status] || { text: status || workflow || "-", color: "grey" };
    },
    canEditDraft(item) {
      const workflow = String(item && item.workflowStatus ? item.workflowStatus : "draft").toLowerCase();
      return !workflow || workflow === "draft";
    },
    openAcceptQuote(item) {
      this.quoteToAccept = item;
      this.quoteAcceptItems = (item.items || []).map((line) => {
        const remaining = Math.max(0, Number(line.quantity || 0) - Number(line.acceptedQuantity || 0));
        return { sourceItemId: line.id, accepted: Number(line.acceptedQuantity || 0), remaining, quantity: remaining, label: `${line.product?.code || ""} · ${line.product?.name || "Artículo"}` };
      }).filter((line) => line.remaining > 0);
      this.acceptQuoteDialog = true;
    },
    async confirmAcceptQuote() {
      if (!this.quoteToAccept) return;
      const quote = this.quoteToAccept;
      this.acceptingQuote = quote.id;
      this.error = "";
      try {
        const acceptedItems = this.quoteAcceptItems.map((line) => ({ sourceItemId: line.sourceItemId, quantity: Number(line.quantity || 0) }));
        if (!acceptedItems.some((line) => line.quantity > 0)) throw new Error("Indicá al menos una cantidad aceptada.");
        const response = await api.post(`/sales/commercial/quotes/${quote.id}/convert-to-order`, { items: acceptedItems, submitForReview: true }, { headers: { "Idempotency-Key": `quote-review-${Date.now()}-${Math.random().toString(16).slice(2)}` } });
        const result = this.unwrap(response) || {};
        this.success = result.message || `Pedido ${result.docNumber || ""} creado desde ${quote.docNumber}.`;
        this.acceptQuoteDialog = false;
        this.quoteToAccept = null;
        this.quoteAcceptItems = [];
        await this.loadAll();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.acceptingQuote = null;
      }
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      this.success = "";
      try {
        const optional = (request) => request.catch(() => ({ data: { data: [] } }));
        const [rowsRes, customersRes, productsRes, warehousesRes, posRes, priceListsRes] = await Promise.all([
          commercialDocumentsService.listBudgets({
            dateFrom: this.historyFilters.dateFrom || undefined,
            dateTo: this.historyFilters.dateTo || undefined,
            originType: this.historyFilters.originType || undefined,
            salesChannel: this.ordersOnly ? "CURRENT_ACCOUNT" : "COUNTER",
          }),
          optional(currentAccountsService.list({ accountType: "customer", isActive: true })),
          optional(productsService.list("")),
          optional(warehousesService.list()),
          optional(pointsOfSaleService.list()),
          optional(priceListsService.list()),
        ]);
        this.rows = this.unwrap(rowsRes) || [];
        this.customers = this.unwrap(customersRes) || [];
        if (!this.customerSuggestions.length) this.customerSuggestions = this.customers.slice(0, 30);
        this.products = this.unwrap(productsRes) || [];
        this.warehouses = this.unwrap(warehousesRes) || [];
        this.pointsOfSale = this.unwrap(posRes) || [];
        this.priceLists = this.unwrap(priceListsRes) || [];
        if (!this.budgetCatalog.priceListId) {
          const activeList = this.priceLists.find((x) => Boolean(x.isActive));
          this.budgetCatalog.priceListId = activeList ? activeList.id : "";
        }
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    addItem() {
      if (!this.itemForm.productId) {
        this.error = "Seleccioná un producto.";
        return;
      }
      const quantity = Number(this.itemForm.quantity || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        this.error = "Cantidad inválida.";
        return;
      }
      this.form.items.push({
        _uid: `${Date.now()}-${Math.random()}`,
        productId: this.itemForm.productId,
        quantity,
        unitPrice: Number(this.itemForm.unitPrice || 0),
        discountPercent: Number(this.itemForm.discountPercent || 0),
        customerBonusPercent: this.form.commercialDiscounts.some(x => x.kind === 'customer') ? null : Number((this.selectedBudgetCustomer || {}).habitualBonusPercent || 0),
        promptPaymentDiscountPercent: 0,
        notes: nullableText(this.itemForm.notes),
      });
      this.itemForm = this.emptyItemForm();
      this.productSearchText = "";
      this.focusProductSearch();
    },
    removeItem(item) {
      this.form.items = this.form.items.filter((x) => x._uid !== item._uid);
    },
    normalizeItemQuantity(item) {
      const quantity = Number(item && item.quantity);
      item.quantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    },
    normalizeCommercialLine(item) {
      const price = Number(item && item.unitPrice);
      const discount = Number(item && item.discountPercent);
      item.unitPrice = Number.isFinite(price) && price >= 0 ? price : 0;
      item.discountPercent = Number.isFinite(discount) ? Math.min(100, Math.max(0, discount)) : 0;
      const customerBonus = Number(item.customerBonusPercent);
      item.customerBonusPercent = Number.isFinite(customerBonus) ? Math.min(100, Math.max(0, customerBonus)) : 0;
      this.normalizeItemQuantity(item);
    },
    buildPayload() {
      if (!this.form.items.length) throw new Error("Debes agregar al menos un ítem.");
      if (!this.directOrder && !this.form.validUntil) throw new Error("Indicá la fecha de validez del presupuesto.");
      return {
        docNumber: nullableText(this.form.docNumber),
        currentAccountId: nullableText(this.form.currentAccountId),
        issueDate: this.form.issueDate,
        validUntil: nullableText(this.form.validUntil),
        status: this.form.status,
        notes: nullableText(this.form.notes),
        promptPaymentDiscountPercent: Number((this.form.commercialDiscounts.find(x => x.kind === 'prompt_payment' && Number(x.percent) === 24) || {}).percent || 0),
        commercialDiscounts: this.form.commercialDiscounts.map(row => ({ kind: row.kind, label: row.label, percent: Number(row.percent) })),
        commercialDiscountChanges: this.form.commercialDiscountChanges,
        showTotal: Boolean(this.form.showTotal),
        salesChannel: this.form.salesChannel || "CURRENT_ACCOUNT",
        documentKind: this.form.documentKind || "quote",
        directOrder: this.directOrder,
        items: this.form.items.map((x) => ({
          productId: x.productId,
          quantity: Number(x.quantity || 0),
          unitPrice: Number(x.unitPrice || 0),
          sourceListPrice: x.sourceListPrice != null && Number(x.unitPrice) === Number(x.catalogUnitPrice) ? Number(x.sourceListPrice) : null,
          discountPercent: Number(x.discountPercent || 0),
          customerBonusPercent: x.customerBonusPercent == null ? null : Number(x.customerBonusPercent),
          promptPaymentDiscountPercent: Number(x.promptPaymentDiscountPercent || 0),
          notes: nullableText(x.notes),
        })),
      };
    },
    async saveRow() {
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = this.buildPayload();
        let successMessage = "";
        if (this.form.id) {
          const response = await commercialDocumentsService.updateBudget(this.form.id, payload);
          const updated = this.unwrap(response) || {};
          successMessage = updated.docNumber ? `Presupuesto ${updated.docNumber} actualizado.` : "Presupuesto actualizado.";
        } else {
          const response = await commercialDocumentsService.createBudget(payload);
          const created = this.unwrap(response) || {};
          successMessage = created.docNumber ? `${this.directOrder ? 'Pedido' : 'Presupuesto'} ${created.docNumber} ${this.directOrder ? 'creado y presentado.' : 'creado.'}` : (this.directOrder ? "Pedido creado y presentado." : "Presupuesto creado.");
          this.createdBudget = created;
          // El alta ya fue confirmada por el servidor: abrimos el modal antes
          // de recargar el historial, que no debe condicionar esta confirmación.
          this.$nextTick(() => {
            this.budgetCreatedDialog = Boolean(created && (created.id || created.docNumber));
          });
        }
        this.resetForm();
        await this.loadAll();
        this.success = successMessage;
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.saving = false;
      }
    },
    async editRow(item) {
      this.loading = true;
      this.error = "";
      this.success = "";
      try {
        const response = await commercialDocumentsService.getBudget(item.id);
        const row = this.unwrap(response);
        this.form = {
          id: row.id,
          documentKind: row.documentKind || "budget",
          salesChannel: row.salesChannel || "COUNTER",
          docNumber: row.docNumber || "",
          currentAccountId: row.currentAccountId || "",
          issueDate: row.issueDate || new Date().toISOString().slice(0, 10),
          validUntil: row.validUntil || "",
          status: row.status || "draft",
          notes: row.notes || "",
          promptPaymentEnabled: Number(row.promptPaymentDiscountPercent || 0) > 0 || (row.items || []).some((x) => Number(x.promptPaymentDiscountPercent || 0) > 0),
          promptPaymentDiscountPercent: Number(row.promptPaymentDiscountPercent || ((row.items || [])[0] || {}).promptPaymentDiscountPercent || 0),
          commercialDiscounts: Array.isArray(row.commercialDiscounts) ? row.commercialDiscounts.map(x => ({ ...x })) : (Number(row.promptPaymentDiscountPercent || 0) > 0 ? [{ kind: 'prompt_payment', label: 'Pronto pago', percent: Number(row.promptPaymentDiscountPercent) }] : []),
          commercialDiscountChanges: Array.isArray(row.commercialDiscountChanges) ? row.commercialDiscountChanges.map(x => ({ ...x })) : [],
          showTotal: row.showTotal !== false && Number(row.showTotal) !== 0,
          items: (row.items || []).map((x) => ({
            _uid: `${x.id}-${Math.random()}`,
            productId: x.productId,
            productCode: x.product && x.product.code ? x.product.code : "",
            productName: x.product && x.product.name ? x.product.name : "",
            quantity: Number(x.quantity || 0),
            unitPrice: Number(x.unitPrice || 0),
            sourceListPrice: x.sourceListPrice == null ? null : Number(x.sourceListPrice),
            catalogUnitPrice: Number(x.unitPrice || 0),
            discountPercent: Number(x.discountPercent || 0),
            customerBonusPercent: x.customerBonusPercent == null ? null : Number(x.customerBonusPercent),
            promptPaymentDiscountPercent: Number(x.promptPaymentDiscountPercent || 0),
            notes: x.notes || "",
          })),
        };
        this.editingOrder = this.ordersOnly;
        this.$nextTick(() => {
          this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
          if (this.form.currentAccountId && this.budgetCatalog.priceListId) this.fetchBudgetCatalog();
        });
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    openConvertDialog(item) {
      this.selectedBudget = item;
      this.convertForm = this.emptyConvertForm();
      this.convertError = "";
      this.convertDialog = true;
    },
    closeConvertDialog() {
      this.convertDialog = false;
      this.selectedBudget = null;
      this.convertError = "";
    },
    async convertBudget() {
      if (!this.selectedBudget || !this.selectedBudget.id) {
        this.convertError = "Seleccioná un presupuesto válido.";
        return;
      }
      this.converting = true;
      this.convertError = "";
      this.error = "";
      this.success = "";
      try {
        const payload = {
          warehouseId: nullableText(this.convertForm.warehouseId),
          issueDate: this.convertForm.issueDate,
          status: "issued",
          mode: this.convertForm.mode,
          paymentMethod: nullableText(this.convertForm.paymentMethod),
          notes: nullableText(this.convertForm.notes),
        };
        const response = await commercialDocumentsService.convertBudgetToCounterInvoice(this.selectedBudget.id, payload);
        const result = this.unwrap(response) || {};
        const invoices = Array.isArray(result.invoices) ? result.invoices.filter(Boolean) : [];
        const invoice = result.counterInvoice || (invoices.length ? invoices[0] : null);
        if (invoices.length > 1) {
          const docNumbers = invoices.map((x) => x.docNumber).filter(Boolean).join(", ");
          this.success = docNumbers
            ? `Presupuesto convertido. Facturas emitidas: ${docNumbers}.`
            : "Presupuesto convertido a facturas mostrador.";
        } else {
          this.success = invoice && invoice.docNumber
            ? `Presupuesto convertido a factura ${invoice.docNumber}.`
            : "Presupuesto convertido a factura mostrador.";
        }
        this.closeConvertDialog();
        await this.loadAll();
        if ((invoice && invoice.id) || invoices.length > 0) {
          this.$router.push({ name: "sales-counter-invoices" });
        }
      } catch (error) {
        this.convertError = this.getError(error);
      } finally {
        this.converting = false;
      }
    },
    openSubmitDialog(item) { this.orderToSubmit = item; this.submitDialog = true; },
    async confirmSubmit() {
      const item = this.orderToSubmit;
      if (!item) return;
      this.sendingToCashier = item.id;
      this.error = "";
      this.success = "";
      try {
        const response = await salesService.sendToCashier(item.id, this.$store.state.auth.user.username);
        const data = response?.data?.data || response?.data || {};
        this.submitSuccessMessage = data.message || (this.ordersOnly ? "El pedido quedó presentado para sus controles comerciales." : "Pedido enviado a caja. Stock reservado.");
        this.submitDialog = false;
        this.submitSuccessDialog = true;
        await this.loadAll();
      } catch (error) {
        const errorMsg = this.getError(error);
        this.error = errorMsg;
        this.activeErrorMsg = errorMsg;
        this.errorDialog = true;
      } finally {
        this.sendingToCashier = null;
        this.orderToSubmit = null;
      }
    },
    async deleteRow(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      const item = this.deleteTarget;
      if (!item) return;
      this.deleting = true;
      try {
        await commercialDocumentsService.deleteBudget(item.id);
        this.deleteDialog = false;
        this.submitSuccessMessage = `${item.docNumber} fue eliminado correctamente.`;
        this.submitSuccessDialog = true;
        await this.loadAll();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.deleting = false;
        this.deleteTarget = null;
      }
    },
    startNewOrder() {
      this.resetForm();
      this.editingOrder = true;
    },
    resetForm() {
      this.form = this.emptyForm();
      this.itemForm = this.emptyItemForm();
      this.budgetCatalogRows = [];
      this.budgetCatalogSuggestions = [];
      this.budgetCatalog.selectedCode = "";
      this.budgetCatalog.searchText = "";
      this.budgetCatalog.exactSelected = false;
      this.editingOrder = false;
    },
    clearDateFilter() {
      this.historyFilters = { dateFrom: "", dateTo: "", originType: "" };
      this.loadAll();
    },

    openQuickCustomer() {
      this.error = "";
      this.success = "";
      this.custForm = this.emptyCustomerForm();
      this.customerDialog = true;
    },
    async saveQuickCustomer() {
      const name = String(this.custForm.name || "").trim();
      if (!name) {
        this.error = "Ingresá el nombre del cliente.";
        return;
      }
      this.savingCustomer = true;
      this.error = "";
      this.success = "";
      try {
        const res = await currentAccountsService.create({
          code: `CLI-${Date.now()}`,
          name,
          taxId: nullableText(this.custForm.taxId),
          taxCondition: nullableText(this.custForm.taxCondition),
          address: nullableText(this.custForm.address),
          phone: nullableText(this.custForm.phone),
          email: nullableText(this.custForm.email),
          accountType: "customer",
          clientType: "CM",
          isActive: true,
        });
        const created = this.unwrap(res);
        await this.loadAll();
        this.form.currentAccountId = created && created.id ? created.id : "";
        this.customerDialog = false;
        this.success = "Cliente creado y seleccionado.";
      } catch (err) {
        this.error = this.getError(err);
      } finally {
        this.savingCustomer = false;
      }
    },
    async previewBudget(item) {
      try {
        this.error = "";
        const res = await salesService.previewBudget(item.id);
        this.previewData = res.data?.data || res.data;
        this.previewDialog = true;
      } catch (err) {
        console.error("Error al previsualizar:", err);
        this.error = "No se pudo previsualizar el pedido.";
      }
    },
    async printBudget(item) {
      try {
        const res = await salesService.downloadBudgetPdf(item.id);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${item.documentKind === "quote" ? "Presupuesto" : "Pedido"}-${item.docNumber}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (err) {
        console.error("Error al imprimir pedido:", err);
        this.error = "No se pudo generar el PDF del pedido.";
      }
    },
    async downloadCreatedBudget() {
      if (!this.createdBudget || !this.createdBudget.id) return;
      this.downloadingBudget = true;
      try {
        await this.printBudget(this.createdBudget);
      } finally {
        this.downloadingBudget = false;
      }
    },
    closeCreatedBudgetDialog() {
      this.budgetCreatedDialog = false;
      this.createdBudget = null;
    },
    openEditCashier(item) {
      this.editCashierError = "";
      this.editCashierForm = {
        id: item.id,
        currentAccountId: item.currentAccountId || "",
        paymentMethod: item.paymentMethod || "",
        posNumber: item.posNumber || null,
        items: (item.items || []).map((x) => ({
          productId: x.productId,
          product: x.product || null,
          productCode: x.product && x.product.code ? x.product.code : "",
          productName: x.product && x.product.name ? x.product.name : "",
          quantity: Number(x.quantity || 0),
          unitPrice: Number(x.unitPrice || 0),
          discountPercent: Number(x.discountPercent || 0),
        })),
      };
      this.editCashierDialog = true;
    },
    async saveEditCashier() {
      this.editCashierSaving = true;
      this.editCashierError = "";
      try {
        await salesService.editFromCashier(this.editCashierForm.id, {
          currentAccountId: this.editCashierForm.currentAccountId || undefined,
          paymentMethod: this.editCashierForm.paymentMethod || undefined,
          posNumber: this.editCashierForm.posNumber || undefined,
          items: this.editCashierForm.items.map((x) => ({
            productId: x.productId,
            quantity: Number(x.quantity),
            unitPrice: Number(x.unitPrice),
            discountPercent: Number(x.discountPercent || 0),
          })),
        });
        this.editCashierDialog = false;
        this.success = "Pedido actualizado desde caja.";
        await this.loadAll();
      } catch (err) {
        this.editCashierError = this.getError(err);
      } finally {
        this.editCashierSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.preview-totals {
  max-width: 280px;
}
.preview-totals > div {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 4px 0;
}
.preview-totals .total {
  color: #1b5e20;
  font-size: 18px;
  padding-top: 8px;
}
.preview-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
</style>
